import { useRecoilValue } from 'recoil';

import { inventoryAtom } from 'src/app/state/inventory';
import { userAtom } from 'src/app/state/user';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';
import { userPermissionsAtom } from 'src/app/state/user-permissions';

import { useContextRegistration } from '../hooks';

import {
  buildOrganizationContext,
  buildPermissionsContext,
  buildProductsData,
  buildProductsSummary,
  buildUserContext,
} from './context-builders';

import type { ContextMetadata } from '../../context/types';

/**
 * Context IDs for Recoil-backed global state
 */
const RECOIL_CONTEXT_IDS = {
  USER_PROFILE: 'user-profile',
  ORG_CONTEXT: 'org-context',
  USER_PERMISSIONS: 'user-permissions',
  PRODUCTS_CATALOG: 'products-catalog',
};

/**
 * Context scopes for different types of Recoil data
 */
const RECOIL_CONTEXT_SCOPES = {
  APP: 'app',
  PRODUCTS: 'products',
};

/**
 * Threshold for determining large product datasets
 */
const LARGE_PRODUCTS_THRESHOLD = 50;

/**
 * Metadata for user-related contexts (stable references to prevent re-registration)
 */
const USER_CONTEXT_METADATA: Partial<ContextMetadata> = {
  type: 'user-data',
  priority: 'high',
  source: 'recoil',
  global: true,
};

/**
 * Metadata for product contexts (stable references to prevent re-registration)
 */
const PRODUCT_CONTEXT_METADATA: Partial<ContextMetadata> = {
  type: 'product-data',
  priority: 'medium',
  source: 'recoil',
  global: false,
};

/**
 * Bridge component that automatically registers Recoil global state as AI context.
 *
 * Captures:
 * - User profile (ID, name, role - but NOT email or sensitive data)
 * - Organization/location context
 * - User permissions
 * - Location features and capabilities
 * - Products catalog data (synced from legacy InventoryStore)
 *
 * All contexts are marked as global (persist across page navigation) and high priority.
 */
export function RecoilResource() {
  const user = useRecoilValue(userAtom);
  const { selectedLocation, selectedCompany, locProfile } = useRecoilValue(userDispensariesAtom);
  const { perms } = useRecoilValue(userPermissionsAtom);
  const inventory = useRecoilValue(inventoryAtom);

  // Register user profile context
  useContextRegistration(
    RECOIL_CONTEXT_IDS.USER_PROFILE,
    () => buildUserContext(user),
    [user],
    RECOIL_CONTEXT_SCOPES.APP,
    USER_CONTEXT_METADATA
  );

  // Register organization/location context
  useContextRegistration(
    RECOIL_CONTEXT_IDS.ORG_CONTEXT,
    () => buildOrganizationContext(selectedLocation, selectedCompany, locProfile),
    [selectedLocation, selectedCompany, locProfile],
    RECOIL_CONTEXT_SCOPES.APP,
    USER_CONTEXT_METADATA
  );

  // Register permissions context
  useContextRegistration(
    RECOIL_CONTEXT_IDS.USER_PERMISSIONS,
    () => buildPermissionsContext(perms),
    [perms],
    RECOIL_CONTEXT_SCOPES.APP,
    USER_CONTEXT_METADATA
  );

  // Register products catalog context
  useContextRegistration(
    RECOIL_CONTEXT_IDS.PRODUCTS_CATALOG,
    () => {
      if (!inventory.products || inventory.products.length === 0) {
        return null;
      }

      const productsCount = inventory.products.length;
      const isLargeDataset = productsCount > LARGE_PRODUCTS_THRESHOLD;

      // For large datasets, create a summary; for smaller datasets, include all products
      return isLargeDataset
        ? buildProductsSummary(inventory.products, inventory.productsLoading)
        : buildProductsData(inventory.products, inventory.productsLoading);
    },
    [inventory.products, inventory.productsLoading],
    RECOIL_CONTEXT_SCOPES.PRODUCTS,
    PRODUCT_CONTEXT_METADATA
  );

  // This component doesn't render anything
  return null;
}
